import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Form Visualizer v1 lexicon entries — 1 tool-level + 2 sub-mode + 4 forms +
 * 7 section types + 2 feedback wrappers = 16 entries. Authored by Bowie in
 * the Form Visualizer v1 scope memo; locked verbatim — do not edit without
 * Bowie review.
 *
 * Outbound cross-link entries (`crosslink-form-to-*`) live in
 * `lexicon-crosslink-entries.ts` to keep the cross-link semantics in one place.
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §12.1
 * Bowie scope memo: .claude/agent-memory/bowie/project_form_visualizer_v1_scope.md
 */
export const FORM_LEXICON_ENTRIES: LexiconEntry[] = [
  // Tool + sub-modes (3)
  {
    id: 'form-visualizer',
    category: 'form',
    displayName: 'Form Visualizer',
    displayNameShort: 'Form',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'See and hear how songs are built — verses, choruses, bridges, and how they connect.',
      intermediate: 'Visualize song structure — sectional architecture across canonical forms.',
      advanced: 'Map song form at the section level — strophic, AABA, verse-chorus, and 12-bar blues archetypes.',
    },
    tagLabel: { beginner: 'song shape', intermediate: 'form + sections', advanced: 'sectional architecture' },
  },
  {
    id: 'form-explore',
    category: 'form',
    displayName: 'Form Explore',
    displayNameShort: 'Explore',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Pick a song shape and hear how each part sounds.',
      intermediate: 'Pick a form and audition each section in context.',
      advanced: 'Free-explore canonical forms; audition section progressions in the form\'s key.',
    },
    tagLabel: { beginner: 'explore', intermediate: 'free explore', advanced: 'form audition' },
  },
  {
    id: 'form-id-quiz',
    category: 'form',
    displayName: 'Form ID Quiz',
    displayNameShort: 'Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Listen to a short song clip and guess its shape.',
      intermediate: 'Identify a form from a compressed 3-section audition.',
      advanced: 'Recognize form from a representative section sequence — train the macro-level perception skill.',
    },
    tagLabel: { beginner: 'guess the shape', intermediate: 'form ID', advanced: 'form recognition' },
  },
  // Forms (4)
  {
    id: 'form-12-bar-blues',
    category: 'form',
    displayName: '12-bar blues',
    displayNameShort: '12-bar',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Twelve measures of a fixed chord pattern — the foundation of blues, R&B, and early rock.',
      intermediate: '12-bar blues — I–IV–V chord cycle over 12 measures; one of the most-played forms in popular music.',
      advanced: 'I–I–I–I–IV–IV–I–I–V–IV–I–V (with turnaround). The 12 bars ARE the form; no internal section labels.',
    },
    tagLabel: { beginner: 'blues', intermediate: '12-bar blues', advanced: 'I–IV–V 12-bar' },
  },
  {
    id: 'form-32-bar-aaba',
    category: 'form',
    displayName: '32-bar AABA',
    displayNameShort: 'AABA',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'A 32-bar shape used in jazz standards — two matching parts, a contrasting middle, then back to the matching part.',
      intermediate: 'AABA — 8 bars × 4 sections; the bridge (B) contrasts with the parallel A sections. The Tin Pan Alley standard.',
      advanced: 'AABA (32-bar). Two parallel 8-bar A-sections, an 8-bar bridge (B / "middle eight") often modulating briefly, return to A. Foundation of jazz-standard repertoire.',
    },
    tagLabel: { beginner: 'jazz standard', intermediate: 'AABA 32', advanced: 'Tin Pan Alley AABA' },
  },
  {
    id: 'form-verse-chorus',
    category: 'form',
    displayName: 'Verse-Chorus',
    displayNameShort: 'V-C',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The shape of most pop songs — verses tell the story, choruses repeat the catchy part.',
      intermediate: 'Verse-Chorus form — alternating sections with a hook chorus that returns multiple times; often with a contrasting bridge.',
      advanced: 'Verse-Chorus form — typically I–V–C–V–C–B–C–O. The dominant form in pop, rock, and contemporary popular music.',
    },
    tagLabel: { beginner: 'pop song', intermediate: 'verse-chorus', advanced: 'V-C form' },
  },
  {
    id: 'form-strophic',
    category: 'form',
    displayName: 'Strophic',
    displayNameShort: 'Strophic',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The same music repeated with different words — like a folk song or hymn.',
      intermediate: 'Strophic form — one section repeated under varying lyrics; no separate chorus.',
      advanced: 'Strophic form — repeated identical musical sections (verses) under varying lyrics. Folk songs, hymns, narrative ballads.',
    },
    tagLabel: { beginner: 'folk song', intermediate: 'strophic', advanced: 'pure repetition' },
  },
  // Section types (7)
  {
    id: 'form-section-intro',
    category: 'form',
    displayName: 'Intro',
    displayNameShort: 'Intro',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The opening — sets up the song.',
      intermediate: 'Intro — opens the song; establishes key, tempo, and feel before the verse begins.',
      advanced: 'Intro — establishes tonal center, tempo, and atmospheric/textural setup. Often vamps tonic or signals key via brief motivic phrase.',
    },
    tagLabel: { beginner: 'opening', intermediate: 'intro', advanced: 'opening section' },
  },
  {
    id: 'form-section-verse',
    category: 'form',
    displayName: 'Verse',
    displayNameShort: 'Verse',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The story part — different words each time it repeats.',
      intermediate: 'Verse — narrative section with changing lyrics; typically lower energy than the chorus.',
      advanced: 'Verse — narrative-bearing section, harmonically supportive (often I–V–vi–IV or similar pop loop), lower dynamic + register than chorus.',
    },
    tagLabel: { beginner: 'the story', intermediate: 'verse', advanced: 'narrative section' },
  },
  {
    id: 'form-section-chorus',
    category: 'form',
    displayName: 'Chorus',
    displayNameShort: 'Chorus',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The catchy part that repeats — usually has the song title.',
      intermediate: 'Chorus — hook section that returns multiple times; typically higher energy than verses.',
      advanced: 'Chorus — hook-bearing section, harmonically higher-energy (vi–IV–I–V is common), elevated register, contains the song\'s main lyrical refrain.',
    },
    tagLabel: { beginner: 'the hook', intermediate: 'chorus', advanced: 'hook section' },
  },
  {
    id: 'form-section-bridge',
    category: 'form',
    displayName: 'Bridge',
    displayNameShort: 'Bridge',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A different part that appears once — gives the song a fresh feel before the chorus returns.',
      intermediate: 'Bridge — contrasting section appearing once per song, typically before the final chorus. New harmony and often new melodic material.',
      advanced: 'Bridge — once-per-song contrasting section; departs harmonically (often via secondary dominants or modal interchange), provides relief before the final chorus return.',
    },
    tagLabel: { beginner: 'the change-up', intermediate: 'bridge', advanced: 'contrasting middle' },
  },
  {
    id: 'form-section-outro',
    category: 'form',
    displayName: 'Outro',
    displayNameShort: 'Outro',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The ending — wraps up the song.',
      intermediate: 'Outro — closing section; may vamp the final chord, fade, or land on a definitive tonic.',
      advanced: 'Outro — closing section. May vamp tonic and fade (radio convention), apply a tag (repeat-and-fade of the chorus hook), or land on a definitive cadence.',
    },
    tagLabel: { beginner: 'ending', intermediate: 'outro', advanced: 'closing section' },
  },
  {
    id: 'form-section-a-jazz',
    category: 'form',
    displayName: 'A section',
    displayNameShort: 'A',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'In AABA, the "A" part is the main theme — it comes back three times.',
      intermediate: 'A section (AABA) — the primary 8-bar phrase; stated, repeated, and returned to after the bridge.',
      advanced: 'A section (32-bar AABA) — 8-bar parallel phrase; the "head" theme. Three statements with the B section between the second and third.',
    },
    tagLabel: { beginner: 'main part', intermediate: 'A section', advanced: 'parallel phrase' },
  },
  {
    id: 'form-section-b-jazz',
    category: 'form',
    displayName: 'B section (bridge)',
    displayNameShort: 'B',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'In AABA, the "B" part — the middle, sounds different from the rest.',
      intermediate: 'B section (AABA) — the bridge; 8-bar contrasting middle. Often called "the middle eight" by jazz musicians.',
      advanced: 'B section (32-bar AABA) — 8-bar contrasting bridge. Often modulates briefly (commonly to IV or vi) or uses secondary-dominant chains before returning to the A section.',
    },
    tagLabel: { beginner: 'middle part', intermediate: 'B / middle 8', advanced: 'bridge (jazz)' },
  },
  // Feedback wrappers (2)
  {
    id: 'form-explore-feedback',
    category: 'form',
    displayName: 'Explore feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Click any section to hear what it sounds like. Try the same section in different forms to compare.',
      intermediate: 'Click sections to audition them. The same section type (e.g., chorus) sounds different across forms — context matters.',
      advanced: 'Section-type identity is harmonic + functional, not absolute — a chorus in verse-chorus form is structurally different from an A section in AABA.',
    },
    tagLabel: { beginner: 'click + listen', intermediate: 'compare sections', advanced: 'contextual identity' },
  },
  {
    id: 'form-quiz-feedback',
    category: 'form',
    displayName: 'Quiz feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Listen for the pattern — does the song keep coming back to the same part (chorus)? Or repeat one part with new words (strophic)?',
      intermediate: 'Form ID hinges on: does a section RETURN? How many DIFFERENT sections are there? Does the energy LIFT for a hook?',
      advanced: 'Form discrimination = return-pattern recognition + section-count tally + harmonic-contrast detection. A returning hook signals verse-chorus or AABA; pure repetition signals strophic; fixed 12-bar harmonic cycle signals blues.',
    },
    tagLabel: { beginner: 'return + lift', intermediate: 'return-pattern', advanced: 'macro discrimination' },
  },
]
